import React from 'react';
import { Card, Spinner, Container, Row, Col } from 'react-bootstrap';

import {
    Address,
    Factory,
    FactoryIndex,
    OffchainAccount,
    OffchainAccountLendingPool,
    PoolTokenType,
    offchain,
} from 'impermax-sdk';
import { useWallet } from 'use-wallet';

import FactoryContext from '../../contexts/Factory';
import PairAddressContext from '../../contexts/PairAddress';
import useAccount from '../../hooks/useAccount';
import useGenericCall from '../../hooks/useGenericCall';
import { PairsGetter, useGetBlacklisted } from '../../hooks/useNetwork';
import { useOffchainAccount } from '../../hooks/useOffchain';
import { AccountOverallStats } from '../OverallStats';
import BorrowPosition from './BorrowPosition';
import MassHarvest from './MassHarvest';
import SupplyPosition from './SupplyPosition';
import './index.scss';

function deleteBlacklistedPositions(
    positions: FactoryIndex<string[]>,
    getBlacklisted: PairsGetter
): FactoryIndex<string[]> {
    if (!getBlacklisted) return null;
    for (const factory of Object.keys(positions) as Factory[]) {
        const blacklisted = getBlacklisted(factory);
        for (let i = 0; i < positions[factory].length; i++) {
            if (blacklisted.includes(positions[factory][i])) {
                positions[factory].splice(i, 1);
                i--;
            }
        }
    }
    return positions;
}

export async function getBorrowPositions(
    offchainAccount: OffchainAccount,
    getBlacklisted: PairsGetter
): Promise<FactoryIndex<string[]>> {
    const borrowPositions = await offchainAccount.getBorrowPositions();
    return deleteBlacklistedPositions(borrowPositions, getBlacklisted);
}

export async function getSupplyPositions(
    offchainAccount: OffchainAccount,
    getBlacklisted: PairsGetter
): Promise<FactoryIndex<string[]>> {
    const supplyPositions = await offchainAccount.getSupplyPositions();
    return deleteBlacklistedPositions(supplyPositions, getBlacklisted);
}

export type OrderedPosition = {
    factory: Factory;
    pair: Address;
    size: number;
};

export async function orderPositions(
    positions: FactoryIndex<Address[]>,
    offchainAccount: OffchainAccount,
    getSize: (offchainAccountLendingPool: OffchainAccountLendingPool) => Promise<number>
): Promise<OrderedPosition[]> {
    const positionsOrdered = [];
    for (const factory of Object.keys(positions) as Factory[]) {
        for (const pair of positions[factory]) {
            const offchainAccountLendingPool = await offchainAccount.getLendingPool(factory, pair);
            const size = await getSize(offchainAccountLendingPool);
            positionsOrdered.push({ factory, pair, size });
        }
    }
    positionsOrdered.sort((a, b) => (a.size > b.size ? -1 : 1));
    return positionsOrdered;
}

export async function getSupplyPositionsOrdered(
    offchainAccount: OffchainAccount,
    getBlacklisted: PairsGetter
): Promise<OrderedPosition[]> {
    const supplyPositions = await getSupplyPositions(offchainAccount, getBlacklisted);
    return orderPositions(
        supplyPositions,
        offchainAccount,
        async (offchainAccountLendingPool) =>
            (await offchainAccountLendingPool.getBorrowableA().getValue()) +
            (await offchainAccountLendingPool.getBorrowableB().getValue())
    );
}

export async function getBorrowPositionsOrdered(
    s: OffchainAccount,
    getBlacklisted: PairsGetter
): Promise<OrderedPosition[]> {
    const borrowPositions = await getBorrowPositions(s, getBlacklisted);
    return orderPositions(borrowPositions, s, async (salp) => salp.getBorrowerEquityValue());
}

export default function AccountPage() {
    const getBlacklisted = useGetBlacklisted();
    const data = useGenericCall(useOffchainAccount(), async (offchainAccount) => ({
        borrowPositionsOrdered: await getBorrowPositionsOrdered(offchainAccount, getBlacklisted),
        supplyPositionsOrdered: await getSupplyPositionsOrdered(offchainAccount, getBlacklisted),
    }));

    const pageAccount = useAccount();
    const { account } = useWallet();

    if (!data)
        return (
            <div className="spinner-container">
                <Spinner animation="border" />
            </div>
        );

    return (
        <>
            <AccountOverallStats />
            <Container>
                {account && pageAccount.toLowerCase() === account.toLowerCase() && <MassHarvest />}
                {data.borrowPositionsOrdered && data.borrowPositionsOrdered.length > 0 && (
                    <>
                        <div className="positions-title">Borrow Positions</div>
                        <Card className="positions">
                            <Row className="positions-header row">
                                <Col xs={12}>Market</Col>
                            </Row>
                            {data.borrowPositionsOrdered.map((data, key: any) => (
                                <FactoryContext.Provider value={data.factory} key={key}>
                                    <PairAddressContext.Provider value={data.pair} key={key}>
                                        <BorrowPosition />
                                    </PairAddressContext.Provider>
                                </FactoryContext.Provider>
                            ))}
                        </Card>
                    </>
                )}
                {data.supplyPositionsOrdered && data.supplyPositionsOrdered.length > 0 && (
                    <>
                        <div className="positions-title">Supply Positions</div>
                        <Card className="positions">
                            <Row className="positions-header">
                                <Col className="market d-none d-md-block">Market</Col>
                                <Col className="position-details">
                                    <Row>
                                        <Col className="supply-balance">Supply Balance</Col>
                                        <Col className="supply-apr">Supply APR</Col>
                                    </Row>
                                </Col>
                            </Row>
                            {data.supplyPositionsOrdered.map((data, key: any) => (
                                <FactoryContext.Provider value={data.factory} key={key}>
                                    <PairAddressContext.Provider value={data.pair} key={key}>
                                        <SupplyPosition />
                                    </PairAddressContext.Provider>
                                </FactoryContext.Provider>
                            ))}
                        </Card>
                    </>
                )}
            </Container>
        </>
    );
}
